import * as Axios from "axios";
import URLJS from "urijs";
import { reportUnexpectedError } from "../../error-reporter";
import { CosmicJSError } from "../domain/help/help-entity";

const DEFAULT_URL = "/cosmicjs/v3/buckets";

export const axiosInstance = Axios.default.create();

export class CosmicJSClient {
  http: Axios.AxiosInstance;
  url: string;

  constructor(bucket: string) {
    this.http = axiosInstance;
    this.url = `${DEFAULT_URL}/${bucket}/objects`;
  }

  protected async fetch<T>(
    query: string,
    props?: string,
    sort?: string,
    limit?: number,
  ): Promise<{ objects: T[] }> {
    const url = new URLJS(this.url);
    url.setQuery("read_key", process.env.REACT_APP_COSMICJS_READ_KEY);
    url.setQuery("query", query);
    url.setQuery("pretty", true);
    if (props) {
      url.setQuery("props", props);
    }
    if (sort) {
      url.setQuery("sort", sort);
    }
    if (limit) {
      url.setQuery("limit", limit);
    }

    return this.fetchWithRetry(url.toString());
  }

  /**
   * CosmicJS の api が頻繁に 503 を返し安定しないため、retry 処理を行う（https://graffer.slack.com/archives/CRHQZCMDF/p1738546161695609）
   */
  private async fetchWithRetry<T>(url: string): Promise<{ objects: T[] }> {
    const maxRetries = 3;

    for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
      try {
        const res = await this.http.get(url);
        return res.data;
      } catch (e) {
        // 404 は早期 return する
        if (e.response?.status === 404) {
          return { objects: [] };
        }

        if (e.response?.status === 503) {
          // 指数バックオフの計算: 1000 * (2 ^ retryCount) ミリ秒
          const delay = 1000 * Math.pow(2, retryCount);
          if (retryCount < maxRetries - 1) {
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        } else {
          reportUnexpectedError(e);
          throw new CosmicJSError({ error: e, code: e.response?.status });
        }
      }
    }
    throw new CosmicJSError({
      error: new Error("Max retries reached"),
      code: "503",
    });
  }
}
